import React, { useState, useMemo } from 'react';
import PositionLedgerRow from './PositionLedgerRow';

// ─── Dashboard palette ────────────────────────────────────────────────────────
const C = {
  bg2:    '#101b2e',
  border: '#1e2d42',
  text:   '#e8edf5',
  body:   '#b8c8dc',
  meta:   '#6a7d95',
  cyan:   '#00c8ff',
  font:   "'DM Mono', ui-monospace, monospace",
};

/**
 * PositionLedgerTable
 * Displays positions in a sortable, filterable table.
 * For option rows: matches each position to the closest preceding PROCEED eval.
 */
function PositionLedgerTable({ positions, assetClass, proceedEvals = [], onOptionClick }) {
  const [sortBy,       setSortBy]       = useState('first_entry_time');
  const [sortOrder,    setSortOrder]    = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');

  const isOption = assetClass === 'option';

  // Match each option position to its closest preceding PROCEED eval
  const evalBySymbol = useMemo(() => {
    if (!isOption || !proceedEvals?.length) return {};
    const map = {};
    positions.forEach(p => {
      const key     = `${p.symbol}-${p.entry_submitted_at_raw || p.first_entry_time}`;
      const entryTs = new Date(p.entry_submitted_at_raw || p.first_entry_time || 0).getTime();
      if (!entryTs) { map[key] = null; return; }
      map[key] = proceedEvals.find(e => new Date(e.t || 0).getTime() <= entryTs) || null;
    });
    return map;
  }, [positions, proceedEvals, isOption]);

  const getMatchedEval = p => {
    const key = `${p.symbol}-${p.entry_submitted_at_raw || p.first_entry_time}`;
    return evalBySymbol[key] || null;
  };

  // Filter
  const filtered = useMemo(() => {
    if (filterStatus === 'all') return positions;
    return positions.filter(p => p.status === filterStatus);
  }, [positions, filterStatus]);

  // Sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av = a[sortBy], bv = b[sortBy];
      if (sortBy.includes('_num') || sortBy === 'quantity') {
        av = parseFloat(av) || 0; bv = parseFloat(bv) || 0;
      }
      if (sortBy.includes('_time') || sortBy.includes('_at')) {
        av = new Date(av || 0).getTime(); bv = new Date(bv || 0).getTime();
      }
      return sortOrder === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  }, [filtered, sortBy, sortOrder]);

  const handleSort = field => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
  };

  // Sort icon (pure text, no Bootstrap icons dependency)
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <span style={{ color: C.meta, marginLeft: 3, fontSize: 8 }}>⇅</span>;
    return <span style={{ color: C.cyan, marginLeft: 3, fontSize: 8 }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  // Filter buttons
  const filterCounts = {
    all:    positions.length,
    active: positions.filter(p => p.status === 'active').length,
    closed: positions.filter(p => p.status === 'closed').length,
  };

  if (positions.length === 0) {
    return (
      <div className="pl-empty">
        No positions to display.
      </div>
    );
  }

  return (
    <div style={{ fontFamily: C.font }}>

      {/* Filter buttons */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 10, alignItems: 'center' }}>
        {['all', 'active', 'closed'].map(status => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            style={{
              fontFamily:   C.font,
              fontSize:     10,
              letterSpacing:'0.05em',
              padding:      '3px 12px',
              borderRadius: 4,
              cursor:       'pointer',
              border:       `1px solid ${filterStatus === status ? C.cyan : C.border}`,
              background:   filterStatus === status ? `${C.cyan}15` : 'transparent',
              color:        filterStatus === status ? C.cyan : C.meta,
              transition:   'all 0.12s',
            }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)} · {filterCounts[status]}
          </button>
        ))}
        {isOption && onOptionClick && (
          <span style={{ fontFamily: C.font, fontSize: 9, color: C.meta, marginLeft: 8 }}>
            Click a row to inspect strategy
          </span>
        )}
      </div>

      {/* Table */}
      <div className="ledger-table-wrap">
        <table className="ledger-table">
          <thead>
            <tr>
              {[
                { label: 'Status',  field: 'status'           },
                { label: 'Symbol',  field: 'symbol'           },
                ...(isOption ? [
                  { label: 'Type',   field: null              },
                  { label: 'Strike', field: 'strike'          },
                  { label: 'Expiry', field: 'expiration'      },
                ] : []),
                { label: 'Dir',    field: 'direction'         },
                { label: 'Qty',    field: 'quantity'          },
                { label: 'Entry$', field: 'avg_entry_price'   },
                { label: 'Fill',   field: null                },
                { label: 'E_Typ',  field: null                },
                { label: 'E_Lmt',  field: null                },
                { label: 'E_Slp',  field: null                },
                { label: 'Exit$',  field: null                },
                { label: 'X_Fill', field: null                },
                { label: 'X_Typ',  field: null                },
                { label: 'X_Lmt',  field: null                },
                { label: 'X_Slp',  field: null                },
                { label: 'P&L',    field: 'total_pnl_num'     },
                { label: 'RR',     field: null                },
                { label: 'TP',     field: null                },
                { label: 'SL Stp', field: null                },
                { label: 'SL Lmt', field: null                },
                { label: 'Hold',   field: 'hold_time_seconds' },
                { label: 'Submit', field: 'first_entry_time'  },
                ...(isOption && onOptionClick ? [{ label: '', field: null }] : []),
              ].map(({ label, field }, i) => (
                <th
                  key={i}
                  onClick={field ? () => handleSort(field) : undefined}
                  style={{ cursor: field ? 'pointer' : 'default' }}
                >
                  {label}{field && <SortIcon field={field} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((position, idx) => (
              <PositionLedgerRow
                key={`${position.symbol}-${idx}`}
                position={position}
                isOption={isOption}
                matchedEval={isOption ? getMatchedEval(position) : null}
                onOptionClick={isOption ? onOptionClick : null}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div style={{ fontFamily: C.font, fontSize: 9, color: C.meta, marginTop: 8 }}>
        Showing {sorted.length} of {positions.length} positions
      </div>

    </div>
  );
}

export default PositionLedgerTable;